/**
 * ProformaTable - Multi-year cash flow projection table
 * 
 * Displays annual cash flows including savings, O&M, depreciation,
 * tax benefits, and cumulative cash position.
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { vortxColors } from '@vortx/shared-ui';

/**
 * Format currency value
 */
function formatCurrency(value, showSign = false) {
    if (value === null || value === undefined) return '-';
    const prefix = showSign && value > 0 ? '+' : '';
    return prefix + value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
}

/**
 * ProformaTable Component
 * 
 * @param {Object} props
 * @param {Array} props.cashflows - Array of annual cashflow objects
 * @param {number} props.totalCapex - Total capital expenditure
 * @param {Object} props.metrics - Summary metrics (NPV, payback, etc.)
 */
function ProformaTable({ cashflows, totalCapex, metrics }) {
    const [expanded, setExpanded] = useState(false);

    if (!cashflows || cashflows.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="textSecondary">No cash flow data available</Typography>
            </Box>
        );
    }

    // Show first 5 years by default, expand for all
    const displayYears = expanded ? cashflows : cashflows.slice(0, 6);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: vortxColors.charcoal, fontWeight: 600 }}>
                    Pro Forma Cash Flows
                </Typography>
                {metrics && (
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box textAlign="center">
                            <Typography variant="caption" color="textSecondary">NPV</Typography>
                            <Typography variant="h6" color={metrics.npv >= 0 ? 'success.main' : 'error.main'}>
                                {formatCurrency(metrics.npv)}
                            </Typography>
                        </Box>
                        <Box textAlign="center">
                            <Typography variant="caption" color="textSecondary">Payback</Typography>
                            <Typography variant="h6">
                                {metrics.paybackPeriod > 0 ? `${metrics.paybackPeriod.toFixed(1)} yrs` : 'N/A'}
                            </Typography>
                        </Box>
                        <Box textAlign="center">
                            <Typography variant="caption" color="textSecondary">IRR</Typography>
                            <Typography variant="h6">
                                {metrics.irr ? `${(metrics.irr * 100).toFixed(1)}%` : 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Year</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Energy Savings</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>O&M Costs</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Depreciation</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Tax Benefit</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Net Cash Flow</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Cumulative</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayYears.map((cf) => (
                            <TableRow
                                key={cf.year}
                                sx={{
                                    bgcolor: cf.year === 0 ? '#fff3e0' : (cf.cumulativeCashflow >= 0 ? 'rgba(76, 175, 80, 0.05)' : 'white'),
                                }}
                            >
                                <TableCell>
                                    <Typography fontWeight={cf.year === 0 ? 600 : 400}>
                                        {cf.year === 0 ? 'Initial' : cf.year}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {cf.year === 0 ? '-' : formatCurrency(cf.savings)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                    {cf.year === 0 ? '-' : formatCurrency(-cf.omCosts)}
                                </TableCell>
                                <TableCell align="right">
                                    {cf.year === 0 ? '-' : formatCurrency(cf.depreciation)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'success.main' }}>
                                    {cf.year === 0 ? '-' : formatCurrency(cf.taxBenefit)}
                                </TableCell>
                                <TableCell align="right">
                                    <Typography
                                        fontWeight={500}
                                        color={cf.netCashflow >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {formatCurrency(cf.netCashflow, true)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography
                                        fontWeight={600}
                                        color={cf.cumulativeCashflow >= 0 ? 'success.main' : 'error.main'}
                                    >
                                        {formatCurrency(cf.cumulativeCashflow)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {cashflows.length > 6 && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <IconButton onClick={() => setExpanded(!expanded)} size="small">
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography variant="caption" color="textSecondary">
                        {expanded ? 'Show less' : `Show all ${cashflows.length - 1} years`}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default ProformaTable;
